import type { ToastProps } from '../../hooks';
import { getErrorMessage, type ErrorResponse } from '../../types/rest';
import { NotificationVariants } from '../Notification';
import type { EntityAdminOperationErrorParams, EntityAdminOperationSuccessParams } from './types';

/**
 * Handles successful entity operations (create, save, delete)
 */
export function handleEntityOperationSuccess(
  params: EntityAdminOperationSuccessParams & {
    addToast: (toast: ToastProps) => string;
    closeEntityAdminDrawer: () => void;
    shouldCloseDrawer?: boolean;
  }
) {
  const { action, isChildEntity, entityName, childEntityName, addToast, closeEntityAdminDrawer, shouldCloseDrawer } =
    params;

  const entityNameForSuccess = isChildEntity && childEntityName ? childEntityName : entityName;

  const actionText = action === 'create' ? 'created' : action === 'delete' ? 'deleted' : 'saved';

  addToast({
    text: `${entityNameForSuccess} ${actionText} successfully.`,
    variant: NotificationVariants.Positive,
  });

  if (shouldCloseDrawer !== false) {
    closeEntityAdminDrawer();
  }
}

/**
 * Handles failed entity operations (create, save, delete)
 */
export function handleEntityOperationError(
  params: EntityAdminOperationErrorParams & {
    addToast: (toast: ToastProps) => string;
  }
) {
  const { action, isChildEntity, entityName, childEntityName, error, addToast } = params;

  const entityNameForError = isChildEntity && childEntityName ? childEntityName : entityName;

  const actionText = action === 'create' ? 'create' : action === 'delete' ? 'delete' : 'save';

  let errorMessage: string | undefined;
  if (error instanceof Error) {
    errorMessage = error.message || undefined;
  } else {
    errorMessage = getErrorMessage(error as ErrorResponse | undefined) ?? error?.toString() ?? undefined;
  }

  const displayEntityName = entityNameForError.toLowerCase();

  const toastText = errorMessage
    ? `Failed to ${actionText} ${displayEntityName}: ${errorMessage}`
    : `Failed to ${actionText} ${displayEntityName}.`;

  addToast({
    text: toastText,
    variant: NotificationVariants.Negative,
  });

  // Note: We intentionally do NOT close the drawer on error - user should be able to fix and retry
}
